import { useTranslation } from 'react-i18next'
import { H3, Text, Button, RadioInput, CheckboxInput } from '@/components/atoms'
import { SelectField } from '@/components/molecules'
import { LANGUAGE } from '@/shared/constants/language'
import { CONTACT_CHANNELS } from '@/shared/constants/onboarding'
import {
  usePetStepTwoPage,
  type UserGender,
} from './page.onboarding.pet-step-two.hook'

export function PetStepTwoPage({
  onContinue,
  onBack,
}: {
  onContinue: () => void
  onBack: () => void
}) {
  const { t } = useTranslation()
  const {
    currentLanguage,
    updateLanguage,
    identifyAs,
    setIdentifyAs,
    identifyOptions,
    channels,
    toggleChannel,
    handleContinue,
    handleBack,
    isFormValid,
  } = usePetStepTwoPage({ onContinue, onBack })

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pb-4 md:flex md:flex-row md:justify-between md:pb-0">
        <div className="flex flex-col justify-between gap-4 md:max-w-98 md:pt-6">
          <div className="flex flex-col gap-4 md:gap-6">
            <H3>{t('onboarding.pet.step2.title')}</H3>
            <Text className="text-neutral-600">
              {t('onboarding.pet.step2.body')}
            </Text>
          </div>
          <div className="hidden gap-3 md:mb-2.5 md:flex">
            <Button
              className="h-12"
              variant="secondary"
              onClick={handleBack}
              data-testid="step-two-back"
            >
              {t('onboarding.pet.back')}
            </Button>
            <Button
              className="h-12"
              onClick={handleContinue}
              disabled={!isFormValid}
              data-testid="step-two-continue"
            >
              {t('onboarding.pet.continue')}
            </Button>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-4 md:mt-0 md:max-w-75 md:gap-8">
          <div className="flex flex-col gap-4">
            <Text>{t('onboarding.pet.step2.languagePreference')}</Text>
            <div className="flex gap-4">
              <RadioInput
                name="language"
                value={LANGUAGE.EN}
                checked={currentLanguage === LANGUAGE.EN}
                onChange={() => updateLanguage(LANGUAGE.EN)}
                label={t('onboarding.pet.step2.english')}
              />
              <RadioInput
                name="language"
                value={LANGUAGE.ES}
                checked={currentLanguage === LANGUAGE.ES}
                onChange={() => updateLanguage(LANGUAGE.ES)}
                label={t('onboarding.pet.step2.spanish')}
              />
            </div>
          </div>

          <SelectField
            label={t('onboarding.pet.step2.identifyAs')}
            value={identifyAs}
            options={identifyOptions}
            placeholder={t('onboarding.pet.step2.identifyAsPlaceholder')}
            onSelect={(v) => setIdentifyAs(v as UserGender)}
            data-testid="step-two-identify-select"
          />

          <div className="flex flex-col gap-4">
            <Text>{t('onboarding.pet.step2.preferredChannels')}</Text>
            <div className="flex flex-wrap gap-4">
              {CONTACT_CHANNELS.map((channel) => (
                <CheckboxInput
                  key={channel}
                  label={t(`onboarding.pet.step2.channels.${channel}`)}
                  checked={channels.has(channel)}
                  onChange={() => toggleChannel(channel)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full gap-3 pt-4 md:hidden">
        <Button
          className="h-12 w-full"
          variant="secondary"
          onClick={handleBack}
          data-testid="step-two-back"
        >
          {t('onboarding.pet.back')}
        </Button>
        <Button
          className="h-12 w-full"
          onClick={handleContinue}
          disabled={!isFormValid}
          data-testid="step-two-continue"
        >
          {t('onboarding.pet.continue')}
        </Button>
      </div>
    </div>
  )
}
